import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { LanguageLevelEnum, LanguageType } from '../../common/types';
import { api } from '../../utils/api-client';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';
import { MATCHING_QUERY_KEY } from '../candidate-project-matching/useRecommendations';
import {
  CandidateAiReviewStateType,
  CandidateAiScalarSuggestionField,
  CandidateAiSetSuggestionField,
  CandidateAiSuggestionField,
} from '../candidates/Candidates.types';

export type CandidateAiReviewActionType =
  | 'ACCEPT_SCALAR'
  | 'REJECT_SCALAR'
  | 'ACCEPT_SET_ITEM'
  | 'REJECT_SET_ITEM';

type CandidateAiScalarReviewActionType = Extract<CandidateAiReviewActionType, 'ACCEPT_SCALAR' | 'REJECT_SCALAR'>;
type CandidateAiSetReviewActionType = Extract<CandidateAiReviewActionType, 'ACCEPT_SET_ITEM' | 'REJECT_SET_ITEM'>;

export type CandidateAiScalarReviewAction = {
  type: CandidateAiScalarReviewActionType;
  field: CandidateAiScalarSuggestionField;
};

export type CandidateAiSetReviewAction = {
  type: CandidateAiSetReviewActionType;
  field: CandidateAiSetSuggestionField;
  value: string | LanguageType;
};

export type CandidateAiReviewAction = CandidateAiScalarReviewAction | CandidateAiSetReviewAction;

export type CandidateAiReviewResult = {
  index: number;
  type: CandidateAiReviewActionType;
  field: CandidateAiSuggestionField;
  status: 'APPLIED' | 'FAILED';
  errorCode?: string;
};

export type CandidateAiReviewResponse = {
  appliedCount: number;
  failedCount: number;
  results: CandidateAiReviewResult[];
};

export type CandidateAiReviewRequest = {
  candidateId: string;
  cvHash: string;
  actions: CandidateAiReviewAction[];
};

export type CandidateAiReviewNotice = {
  kind: 'conflict' | 'invalid-request' | 'generic' | 'partial-success' | 'partial-failure';
  message: string;
  code?: string;
  details?: string[];
  hiddenDetailsCount?: number;
};

const candidateAiSuggestionFieldLabels: Record<CandidateAiScalarSuggestionField, string> = {
  summary: 'Summary',
  professionalExperienceSince: 'Professional experience since',
  seniority: 'Seniority',
  city: 'City',
};

const candidateAiSetFieldLabels: Record<CandidateAiSetSuggestionField, string> = {
  technologies: 'Technologies',
  languages: 'Languages',
};

type ErrorMessages = {
  messages?: Record<string, string>;
};

export const mapCandidateAiReviewError = (
  error: AxiosError<ErrorMessages>
): CandidateAiReviewNotice => {
  const status = error.response?.status;
  const reviewCode = error.response?.data?.messages?.candidateAiReview;

  if (status === 409) {
    if (reviewCode === 'suggestion-not-available') {
      return {
        kind: 'conflict',
        code: reviewCode,
        message: 'This CV suggestion is no longer available. Candidate details were refreshed.',
      };
    }

    if (reviewCode === 'stale-cv-hash') {
      return {
        kind: 'conflict',
        code: reviewCode,
        message: 'CV suggestions changed. Candidate details were refreshed.',
      };
    }

    if (reviewCode === 'stale-suggestions') {
      return {
        kind: 'conflict',
        code: reviewCode,
        message: 'These CV suggestions are from a previous CV. Candidate details were refreshed.',
      };
    }

    return {
      kind: 'conflict',
      code: reviewCode,
      message: 'CV suggestions changed. Candidate details were refreshed.',
    };
  }

  if (status === 400) {
    return {
      kind: 'invalid-request',
      code: reviewCode,
      message: 'Review action could not be completed. Please refresh and try again.',
    };
  }

  return {
    kind: 'generic',
    code: reviewCode,
    message: 'Something went wrong while reviewing CV suggestions. Try again in a moment.',
  };
};

export const getCandidateAiReviewCvHash = (candidate: CandidateAiReviewStateType) => candidate.aiSuggestions.cvHash || '';

export const getCandidateAiSuggestionFieldLabel = (field: CandidateAiSuggestionField) =>
  candidateAiSuggestionFieldLabels[field as CandidateAiScalarSuggestionField] ??
  candidateAiSetFieldLabels[field as CandidateAiSetSuggestionField];

export const formatCandidateAiLanguageValue = (value: LanguageType) => {
  const level = LanguageLevelEnum[value.level as keyof typeof LanguageLevelEnum] ?? value.level;
  return `${value.language} · ${level}`;
};

export const formatCandidateAiReviewActionValue = (action: CandidateAiReviewAction) => {
  if (!('value' in action)) {
    return getCandidateAiSuggestionFieldLabel(action.field);
  }

  if (action.field === 'languages') {
    return formatCandidateAiLanguageValue(action.value as LanguageType);
  }

  return String(action.value);
};

export const mapCandidateAiReviewResultErrorCode = (errorCode?: string, field?: CandidateAiSuggestionField) => {
  if (errorCode === 'invalid-action') {
    return 'This action is no longer valid for the current suggestion.';
  }

  if (errorCode === 'invalid-value') {
    return 'This suggestion contains an invalid value.';
  }

  if (errorCode === 'suggestion-not-available') {
    return 'This CV suggestion is no longer available.';
  }

  if (errorCode === 'city-invalid-for-autocreate') {
    return 'This city value is invalid for auto-create.';
  }

  if (errorCode === 'technologies-invalid-for-autocreate') {
    return 'This technology value is invalid for auto-create.';
  }

  if (errorCode === 'languages-invalid-for-autocreate') {
    return 'This language value is invalid for auto-create.';
  }

  if (field === 'city') {
    return 'This city suggestion could not be applied.';
  }

  if (field === 'technologies') {
    return 'This technology suggestion could not be applied.';
  }

  if (field === 'languages') {
    return 'This language suggestion could not be applied.';
  }

  return 'This CV suggestion could not be applied.';
};

export const formatCandidateAiReviewFailedItem = (action: CandidateAiReviewAction, errorCode?: string) =>
  `${formatCandidateAiReviewActionValue(action)} - ${mapCandidateAiReviewResultErrorCode(errorCode, action.field)}`;

export const useCandidateAiReviewActions = () => {
  const queryClient = useQueryClient();
  return useMutation<CandidateAiReviewResponse, AxiosError<ErrorMessages>, CandidateAiReviewRequest>(
    async ({ candidateId, cvHash, actions }) => {
      const response = await api.post<CandidateAiReviewResponse>(
        pathToUrl(ApiRoutesEnum.candidateAiReviewActions, { id: candidateId }),
        {
          cvHash,
          actions,
        }
      );

      return response.data;
    },
    {
      onSuccess: (response, request) => {
        const accepted = response.results.some(
          (result) => result.status === 'APPLIED' && request.actions[result.index]?.type.startsWith('ACCEPT_')
        );
        if (accepted) void queryClient.invalidateQueries([MATCHING_QUERY_KEY]);
      },
    }
  );
};
